import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/features/auth";
import { getDashboardLayout } from "@/features/dashboard/services/dashboardLayout.service";
import type { DashboardLayoutResponse } from "@/features/dashboard/types/dashboardLayout";

type UseDashboardLayoutReturn = {
  data: DashboardLayoutResponse | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
};

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * Creates the query key for dashboard layout.
 * Includes organization ID for proper cache isolation per company.
 * This ensures admin impersonation and organization switches get fresh data.
 */
export const createDashboardLayoutQueryKey = (organizationId: string | undefined) =>
  ["dashboard-layout", organizationId ?? "anonymous"] as const;

/**
 * Hook to fetch the dashboard layout for the current user's organization.
 * The backend handles layout selection via DashboardLayoutResolver:
 * - Returns custom layout if configured for the organization
 * - Falls back to system default otherwise
 */
export const useDashboardLayout = (): UseDashboardLayoutReturn => {
  const { isAuthenticated, organization } = useAuth();
  const organizationId = organization?.id;

  const query = useQuery({
    queryKey: createDashboardLayoutQueryKey(organizationId),
    queryFn: getDashboardLayout,
    staleTime: FIVE_MINUTES_MS,
    gcTime: FIVE_MINUTES_MS * 2,
    retry: 2,
    enabled: isAuthenticated && !!organizationId,
  });

  return {
    data: query.data,
    isLoading: query.isLoading || !organizationId,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
